import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { position, space } from 'styled-system';

import { StateType } from '../../../enums/StateType';
import { pointerEvents, themeRight, themeTop } from '../../../theme/system-utilities';
import { Flex } from '../../Flex';
import StateIcon from './StateIcon';
import StateMessage from './StateMessage';
import type { InputStateMessage } from './types';
import { getStateType } from './utils';

interface StateMessagesProps extends HasStateProps, MessagesProps {
  id?: string;
  withIcon?: boolean;
  withMessagesIcon?: boolean;
}

interface HasStateProps {
  hasError?: boolean;
  hasWarning?: boolean;
  hasSuccess?: boolean;
  isLoading?: boolean;
}

interface MessagesProps {
  errors?: string[];
  warnings?: string[];
  successes?: string[];
  messages?: InputStateMessage[];
}

const ICON_SIZE = 32;

const UnstyledList = styled(Flex)<any>`
  ${space};
`;

const List = (props: ComponentProps<typeof UnstyledList>) => (
  <UnstyledList as="ul" flexDirection="column" m="sp_0" mt="sp_4" p="sp_0" {...props} />
);

const UnstyledStyledStateIcon = styled(StateIcon)<any>`
  ${position};
  ${themeTop};
  ${themeRight};
  ${pointerEvents};
`;

const StyledStateIcon = (props: ComponentProps<typeof UnstyledStyledStateIcon>) => (
  <UnstyledStyledStateIcon
    position="absolute"
    themeTop={['sp_4', null, null, 'sp_6']}
    themeRight="sp_4"
    pointerEvents="none"
    size={ICON_SIZE}
    {...props}
  />
);

const useMessagesWithType = ({ errors, warnings, successes, messages }: Required<MessagesProps>) =>
  useMemo(
    () =>
      messages.length > 0
        ? messages
        : [
            ...errors.map(message => ({ message, type: StateType.Error })),
            ...warnings.map(message => ({ message, type: StateType.Warning })),
            ...successes.map(message => ({ message, type: StateType.Success })),
          ],
    [errors, warnings, successes, messages]
  );

// eslint-disable-next-line complexity
const StateMessages = ({
  id,
  withMessagesIcon = true,
  withIcon = false,
  hasError = false,
  hasWarning = false,
  hasSuccess = false,
  isLoading = false,
  errors = [],
  warnings = [],
  successes = [],
  messages = [],
}: StateMessagesProps) => {
  const stateMessages = useMessagesWithType({
    errors,
    warnings,
    successes,
    messages,
  });

  const isStateApplied = !withIcon && (hasError || hasWarning || hasSuccess || isLoading);

  return (
    <div id={id} aria-live="polite">
      {isStateApplied && (
        <StyledStateIcon type={getStateType({ hasError, hasWarning, hasSuccess, isLoading })} />
      )}
      {stateMessages.length > 0 && (
        <List>
          {stateMessages.map(({ message, type }) => (
            <StateMessage
              key={`${type} ${message}`}
              type={type}
              withIcon={withMessagesIcon && withIcon}
            >
              {message}
            </StateMessage>
          ))}
        </List>
      )}
    </div>
  );
};

export default StateMessages;
